<template>
  <div class="donation-feed-section" :style="primaryColorVar">
    <div v-if="shouldShowFilters" class="sorting-section flex flex-row gap-3 items-center mb-5">
      <div
        class="search-bar border border-gray-disabled border-solid box-border flex gap-2 items-center rounded-lg flex-1 bg-white hover:border-blue-04 transition-all duration-200 focus-within:border-blue-04"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M13.0006 6.50032C13.0006 7.93476 12.535 9.25983 11.7506 10.3349L15.707 14.2944C16.0977 14.6851 16.0977 15.3195 15.707 15.7101C15.3164 16.1008 14.682 16.1008 14.2913 15.7101L10.3349 11.7506C9.25983 12.535 7.93476 13.0006 6.50032 13.0006C2.90952 13.0006 0 10.0911 0 6.50032C0 2.90952 2.90952 0 6.50032 0C10.0911 0 13.0006 2.90952 13.0006 6.50032ZM6.50032 11.0005C8.98481 11.0005 11.0005 8.98481 11.0005 6.50032C11.0005 4.01582 8.98481 2.0001 6.50032 2.0001C4.01582 2.0001 2.0001 4.01582 2.0001 6.50032C2.0001 8.98481 4.01582 11.0005 6.50032 11.0005Z"
            fill="#B8B8D4"
          />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Type to search"
          class="flex-1 border-none outline-none font-nunito-sans font-normal text-base leading-normal text-black-00 placeholder-blue-04"
          @input="onSearchInput"
        />
      </div>
      <div
        class="sort-dropdown border border-gray-disabled border-solid box-border flex gap-2 h-10 items-center p-2 rounded-lg bg-white hover:border-blue-04 transition-all duration-200 cursor-pointer relative shrink-0 w-auto"
        @click="focusSelect"
      >
        <div class="flex items-center justify-center w-6 h-6 p-1 shrink-0 pointer-events-none">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.70721 15.7062L7.70634 12.7063C8.09685 12.3156 8.09685 11.6812 7.70634 11.2906C7.31582 10.9 6.68164 10.9 6.29112 11.2906L5.00088 12.5844V1C5.00088 0.446875 4.55413 0 4.00117 0C3.44821 0 3.00146 0.446875 3.00146 1V12.5844L1.70809 11.2906C1.31758 10.9 0.683393 10.9 0.292883 11.2906C-0.0976276 11.6812 -0.0976276 12.3156 0.292883 12.7063L3.292 15.7062C3.68251 16.0969 4.3167 16.0969 4.70721 15.7062ZM12.7049 0.29375C12.3144 -0.096875 11.6802 -0.096875 11.2897 0.29375L8.29054 3.29375C7.90003 3.68437 7.90003 4.31875 8.29054 4.70937C8.68105 5.1 9.31524 5.1 9.70575 4.70937L10.9991 3.41563V15C10.9991 15.5531 11.4459 16 11.9988 16C12.5518 16 12.9985 15.5531 12.9985 15V3.41563L14.2919 4.70937C14.6824 5.1 15.3166 5.1 15.7071 4.70937C16.0976 4.31875 16.0976 3.68437 15.7071 3.29375L12.708 0.29375H12.7049Z"
              fill="#B8B8D4"
            />
          </svg>
        </div>
        <select ref="sortSelect" v-model="sortBy" @change="onSortChange" class="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="amount_high">High to low</option>
          <option value="amount_low">Low to high</option>
          <option value="name_az">Name (A-Z)</option>
          <option value="name_za">Name (Z-A)</option>
        </select>
        <div class="sort-text-label font-nunito-sans font-semibold text-base leading-normal text-black pointer-events-none whitespace-nowrap">
          {{ sortDisplayText }}
        </div>
        <div class="w-6 h-6 flex items-center justify-center shrink-0 pointer-events-none">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1l4 4 4-4" stroke="#b8b8d4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </div>
    </div>
    <div class="recent-donations flex flex-col gap-3">
      <div v-if="isLoading && !donors.length" class="flex justify-center items-center py-4">
        <div class="text-gray">Loading donations...</div>
      </div>
      <div
        v-for="donation in donors"
        :key="donation.id"
        class="donor-item flex flex-col bg-white rounded-lg donation-card-shadow gap-2 p-4"
        :class="{ 'donation-enter': donation._isNew }"
      >
        <div class="flex items-center gap-2">
          <div class="bg-primary-opacity rounded-lg p-3">
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17.9886 4.39249C17.9571 3.19736 17.4561 2.06368 16.5955 1.24037C15.7349 0.417069 14.5852 -0.0285344 13.3987 0.00141796C12.5287 -0.00851501 11.6737 0.229999 10.9327 0.689347C10.1917 1.14869 9.59501 1.81009 9.21174 2.59693C9.19269 2.63725 9.16269 2.67129 9.12521 2.69513C9.08773 2.71897 9.04433 2.73163 9 2.73163C8.95567 2.73163 8.91224 2.71897 8.87476 2.69513C8.83728 2.67129 8.80728 2.63725 8.78823 2.59693C8.40495 1.81009 7.80828 1.14869 7.06727 0.689347C6.32627 0.229999 5.47125 -0.00851501 4.60124 0.00141796C3.41479 -0.0285344 2.26502 0.417069 1.40445 1.24037C0.543879 2.06368 0.0428644 3.19736 0.0113956 4.39249C-0.337476 11.4026 7.42979 15.3206 8.79563 15.9547C8.85949 15.9845 8.92905 16 8.99945 16C9.06985 16 9.13941 15.9845 9.20327 15.9547C10.5702 15.3217 18.3375 11.4026 17.9886 4.39249Z"
                fill="var(--primary-color)"
              />
            </svg>
          </div>
          <div class="donor-name flex-1 font-nunito-sans font-semibold text-base leading-normal text-black">
            {{ donorName(donation) }}
          </div>
          <div class="donation-amount flex items-center">
            <template v-if="showDescription">
              <span class="font-nunito-sans font-bold text-base">{{ donation.amountDescription || '' }}</span>
            </template>
            <template v-else>
              <app-price :amount="donation.displayAmount" :currency="donation.currency" :frequency="donation.frequency" />
              <frequency-hint :frequency="donation.frequency" />
            </template>
          </div>
        </div>
        <div v-if="donationFundraiser(donation)?.slug" class="fundraiser-info flex items-center gap-1 py-1">
          <span class="mr-4 font-nunito-sans font-semibold text-sm leading-normal text-gray">With</span>
          <span
            class="font-nunito-sans font-bold text-sm leading-normal text-black underline cursor-pointer hover:opacity-80"
            @click="navigateToFundraiser(donationFundraiser(donation).slug)"
          >
            {{ fundraiserDisplayName(donationFundraiser(donation)) }}
          </span>
        </div>
        <div v-if="donation.publicDedicationMessage" class="bg-off-white rounded-lg py-2 px-3">
          <div class="comment-public font-nunito-sans font-semibold text-sm leading-normal text-black-01">"{{ donation.publicDedicationMessage }}"</div>
        </div>
      </div>

      <div v-if="donors.length === 0 && !isLoading" class="text-center py-3">
        <div class="text-gray">
          {{ searchQuery.length > 0 ? 'No donations found for your search' : 'No donations yet' }}
        </div>
      </div>

      <button
        v-if="hasMoreDonations"
        type="button"
        class="view-all-button flex justify-center items-center gap-1 py-2 px-3 mt-2 text-black-01 font-nunito-sans font-bold text-base h-10 self-center"
        @click="loadMoreDonations"
        :disabled="isLoading"
        @mousedown.prevent
      >
        <span v-if="isLoading" class="inline-block mr-2">
          <svg class="animate-spin h-4 w-4 text-black-01" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </span>
        See more
      </button>
    </div>
  </div>
</template>

<script>
import { fetchRecentDonations } from '@/services/donationFeed';
import AppPrice from '@/common/AppPrice.vue';
import FrequencyHint from '@/common/FrequencyHint.vue';

export default {
  name: 'DonationFeed',

  components: {
    AppPrice,
    FrequencyHint
  },

  props: {
    campaignId: {
      type: String,
      default: null
    },
    campaignSettings: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      donors: [],
      isLoading: false,
      totalDonations: 0,
      pageSize: 10,
      searchQuery: '',
      sortBy: 'newest',
      searchTimeout: null,
      pollInterval: null,
      donationQueue: [],
      queueTimer: null,
      latestDonationDate: null
    };
  },

  computed: {
    primaryColorVar() {
      if (!this.campaignSettings) {
        return {};
      }
      return {
        '--primary-color-values': this.campaignSettings.themeColor,
        '--primary-color': 'var(--primary-color-values)',
        '--primary-color-opacity-values': `color-mix(in srgb, ${this.campaignSettings.themeColor} 10%, white)`,
        '--primary-color-opacity': 'var(--primary-color-opacity-values)',
        '--secondary-color-values': this.campaignSettings.headerColor,
        '--secondary-color': 'var(--secondary-color-values)',
        '--primary-color-text-values': this.campaignSettings.themeTextColor,
        '--primary-color-text': 'var(--primary-color-text-values)'
      };
    },
    // Special campaigns that show description instead of amount
    showDescription() {
      return ['22aa5a50-9466-11f0-9c2b-638e32a2e1a1', 'fe4d5f80-b329-11f0-a1a9-f15a9235b3e3'].includes(this.campaignId);
    },

    hasMoreDonations() {
      return this.donors.length < this.totalDonations;
    },

    sortDisplayText() {
      const sortTexts = {
        newest: 'Newest first',
        oldest: 'Oldest first',
        amount_high: 'High to low',
        amount_low: 'Low to high',
        name_az: 'Name (A-Z)',
        name_za: 'Name (Z-A)'
      };
      return sortTexts[this.sortBy];
    },

    // Show search/sort when there are donations OR when user has an active search/filter
    shouldShowFilters() {
      return this.totalDonations > 0 || this.searchQuery.length > 0 || this.sortBy !== 'newest';
    }
  },

  methods: {
    donorName(donation) {
      if (donation?.donor) {
        return `${donation.donor.firstName} ${donation.donor.lastName}`;
      }
      return 'Anonymous';
    },
    donationFundraiser(donation) {
      return donation?.user_org || donation?.fundraiser || null;
    },
    fundraiserDisplayName(fundraiser) {
      const user = fundraiser?.user || fundraiser?.supporter;
      return fundraiser?.nickname || fundraiser?.fundraiserInfo?.nickname || user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    },

    navigateToFundraiser(slug) {
      // Since we don't have router in the widget, we can emit an event
      // or handle this differently based on the widget's context
      console.log('Navigate to fundraiser:', slug);
      // Could emit an event for the parent page to handle
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'navigateToFundraiser',
            slug
          },
          '*'
        );
      }
    },

    // Fetch donations from the server
    async fetchDonations(resetList = false) {
      // If no campaign ID is provided, we still need to fetch some default data
      // The API service will try to find a campaign ID from the store
      this.isLoading = true;

      try {
        const params = {
          offset: resetList ? 0 : this.donors.length,
          limit: this.pageSize,
          search: this.searchQuery.trim() || null,
          sortBy: this.sortBy
        };

        const result = await fetchRecentDonations(params, null, this.campaignId);

        // Server returns {rows, count} directly in the response
        const { rows, count } = result;
        if (resetList) {
          this.donors = rows;
        } else {
          this.donors = [...this.donors, ...rows];
        }
        this.totalDonations = count;

        // Track the newest donation timestamp for polling
        if (rows.length > 0 && this.sortBy === 'newest' && rows[0].createdAt) {
          this.latestDonationDate = rows[0].createdAt;
        }
      } catch (error) {
        console.error('Failed to fetch donations:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // Load more donations when the user clicks "See more"
    loadMoreDonations() {
      if (!this.isLoading && this.hasMoreDonations) {
        this.fetchDonations();
      }
    },

    // Handle search input with debouncing
    onSearchInput() {
      console.log('Search input changed:', this.searchQuery);
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        console.log('Executing search with query:', this.searchQuery);
        this.fetchDonations(true); // Reset list when searching
      }, 300); // 300ms debounce
    },

    // Handle sort change
    onSortChange() {
      console.log('Sort changed to:', this.sortBy);
      this.fetchDonations(true); // Reset list when sorting changes
    },

    // Focus the select dropdown
    focusSelect() {
      if (this.$refs.sortSelect) {
        this.$refs.sortSelect.focus();
        this.$refs.sortSelect.click();
      }
    },

    async initializeFeed() {
      // Try to fetch immediately first
      try {
        await this.fetchDonations();
      } catch (error) {
        console.warn('First fetch attempt failed, retrying in 500ms...', error.message);
        // If the first attempt fails (likely due to store not ready), retry after a delay
        setTimeout(async () => {
          try {
            await this.fetchDonations();
          } catch (retryError) {
            console.error('Retry failed, giving up:', retryError.message);
          }
        }, 500);
      }

      this.startPolling();
    },

    startPolling() {
      this.pollInterval = setInterval(() => {
        this.pollNewDonations();
      }, 10000);
    },

    async pollNewDonations() {
      // Only poll when viewing the default newest sort with no search
      if (this.searchQuery || this.sortBy !== 'newest') return;

      try {
        const params = {
          offset: 0,
          limit: 20,
          sortBy: 'newest',
          since: this.latestDonationDate
        };
        const result = await fetchRecentDonations(params, null, this.campaignId);
        const { rows, count } = result;

        this.totalDonations = count;

        if (rows.length > 0) {
          // Update latest timestamp from newest row
          this.latestDonationDate = rows[0].createdAt;
          // Reverse so oldest new donations are processed first (chronological order)
          this.donationQueue.push(...rows.reverse());
          this.processQueue();
          // Notify other widgets (e.g. progress bar) that new donations arrived
          window.dispatchEvent(new Event('new-donations'));
        }
      } catch (error) {
        console.error('Poll failed:', error);
      }
    },

    processQueue() {
      if (this.donationQueue.length === 0 || this.queueTimer) return;

      const donation = this.donationQueue.shift();
      donation._isNew = true;
      this.donors.unshift(donation);

      if (this.donationQueue.length > 0) {
        this.queueTimer = setTimeout(() => {
          this.queueTimer = null;
          this.processQueue();
        }, 2000);
      }
    }
  },

  mounted() {
    this.initializeFeed();
  },

  beforeDestroy() {
    clearInterval(this.pollInterval);
    clearTimeout(this.queueTimer);
    clearTimeout(this.searchTimeout);
  }
};
</script>
